import { Injectable, EventEmitter, signal } from '@angular/core';
import { Observable, Subject, from, mergeMap } from 'rxjs';
import { PouchDB } from '../db/pouchdb-init';
import { BasePouchDbService } from '../db/base-pouchdb.service';
import { BaseDocument, SyncProgress, SaveDocumentOptions } from '../types';

/**
 * Generische Basis-Implementierung für Dokument-Services mit PouchDB.
 * Bietet universelle CRUD-Operationen, Sync-Funktionalität und Offline-Support
 * für beliebige CouchDB-Dokumente.
 *
 * Diese Klasse ist komplett generisch und enthält keine App-spezifische Logik.
 * Subklassen können spezialisierte Funktionalität hinzufügen (z.B. räumliche
 * Filterung, Permissions, Views).
 */
@Injectable()
export abstract class BaseDocumentService<T extends BaseDocument> extends BasePouchDbService {
	// Abstrakte Service-Getter - Subklassen müssen diese implementieren
	// Typen sind any, da die konkreten Services app-spezifisch sind
	protected abstract get storage(): any; // StorageService
	protected abstract get oms(): any; // OfflineModeService
	protected abstract get ps(): any; // ProjectProvider

	// Optionale Services - Subklassen können diese überschreiben
	protected get msg(): any | null { return null; }
	protected get ts(): any | null { return null; }

	// Public Properties
	public isBusy = false;
	public override isInitialized = false;
	public loadingProgress = 0;
	public loadingProgressText = '';
	public lastDownloadedDocumentsCount = 0;

	// Rate-Limiting für DB-Operationen (verhindert IP-Sperren)
	private dbOperationQueue: (() => Promise<any>)[] = [];
	private isProcessingQueue = false;
	private readonly DB_OPERATION_DELAY_MS = 50; // Mindestabstand zwischen Operationen

	// PouchDB Backoff-Funktion für Retry (exponentiell mit Maximum)
	private readonly pouchDbBackoff = (delay: number) => {
		// Exponentieller Backoff: 1s, 2s, 4s, 8s, ... max 60s
		if (delay === 0) return 1000;
		return Math.min(delay * 2, 60000);
	};

	// Events (generische Namen)
	checkedOut = new EventEmitter<void>(true);
	checkedIn = new EventEmitter<void>(true);
	discarded = new EventEmitter<void>(true);
	initialized = new EventEmitter<void>(true);
	documentSaved = new EventEmitter<T>(true);
	documentsDownloadCompleted = new EventEmitter<void>(true);
	downloadedDocumentsDiscarded = new EventEmitter<void>(true);
	documentsRefreshRequested = new EventEmitter<void>(true);
	connected = new EventEmitter<string>(true);
	connectionFailed = new EventEmitter<void>(true);

	protected _documentsDownloaded = false;

	// Sync-Fortschritt für alle Projekte
	syncProgressList = signal<SyncProgress[]>([]);
	isSyncing = signal<boolean>(false);

	// True wenn es ungesynchronisierte lokale Änderungen gibt
	hasPendingChanges = signal<boolean>(false);

	currentDb_ = new Subject<PouchDB.Database>();

	// Dynamischer Netzwerkstatus - wird vom OfflineModeService aktualisiert
	get isConnected(): boolean {
		return this.oms.networkOnline?.() ?? false;
	}

	/**
	 * Wartet auf die Initialisierung des Service.
	 * Gibt ein Promise zurück, das sofort resolved wenn bereits initialisiert,
	 * ansonsten wenn das initialized-Event emitted wird.
	 */
	waitForInitialization(): Promise<void> {
		if (this.isInitialized) return Promise.resolve();
		return new Promise<void>((resolve) => {
			const sub = this.initialized.subscribe(() => {
				sub.unsubscribe();
				resolve();
			});
		});
	}

	/**
	 * Führt eine DB-Operation mit Rate-Limiting aus.
	 * Verhindert zu viele gleichzeitige Anfragen (IP-Sperren-Schutz).
	 */
	protected async queueDbOperation<U>(operation: () => Promise<U>): Promise<U> {
		return new Promise<U>((resolve, reject) => {
			this.dbOperationQueue.push(async () => {
				try {
					const result = await operation();
					resolve(result);
					return result;
				} catch (error) {
					reject(error);
					throw error;
				}
			});
			this.processDbQueue();
		});
	}

	private async processDbQueue(): Promise<void> {
		if (this.isProcessingQueue || this.dbOperationQueue.length === 0) return;
		this.isProcessingQueue = true;

		while (this.dbOperationQueue.length > 0) {
			const operation = this.dbOperationQueue.shift();
			if (operation) {
				try {
					await operation();
				} catch { /* Fehler werden in queueDbOperation behandelt */ }
				// Verzögerung zwischen Operationen (nur wenn weitere Operationen warten)
				if (this.dbOperationQueue.length > 0) {
					await new Promise(r => setTimeout(r, this.DB_OPERATION_DELAY_MS));
				}
			}
		}

		this.isProcessingQueue = false;
	}

	protected setProgress(result: any) {
		const max = result.docs_read + (result.pending ? result.pending : 0);
		const cur = result.docs_read;
		this.loadingProgress = 100 * cur / max;
		this.loadingProgressText = `${cur} / ${max} Dokumente gelesen`;
	}

	// ========== CRUD Operations ==========

	/**
	 * Lädt ein einzelnes Dokument.
	 * Subklassen können Transformation über transformAfterLoad() hinzufügen.
	 */
	async get(id: string): Promise<T | undefined> {
		try {
			const doc = await this.db()?.get(id);
			return doc ? this.transformAfterLoad(doc as T) : undefined;
		} catch (error: any) {
			if (error?.status === 404) return undefined;
			throw error;
		}
	}

	/**
	 * Lädt alle Dokumente.
	 * Subklassen müssen queryIndex() implementieren oder überschreiben.
	 */
	async getAll(): Promise<T[]> {
		const result = await this.queryIndex(true);
		const docs = result?.rows?.map((row: any) => row.doc as T) ?? [];
		return docs.map((doc: T) => this.transformAfterLoad(doc));
	}

	/**
	 * Speichert ein Dokument.
	 */
	async put(doc: T): Promise<T> {
		const database = this.db();
		if (!database) {
			throw new Error('Keine Datenbankverbindung');
		}
		const docToSave = this.prepareDocumentForSave(doc);
		const result = await database.put(docToSave);
		(doc as any)._rev = result.rev;
		// Markiere dass ungesynchronisierte Änderungen existieren
		if (this.isLiveSync() || this.oms.userOfflineMode()) {
			this.hasPendingChanges.set(true);
		}
		return doc;
	}

	/**
	 * Lädt mehrere Dokumente.
	 */
	async bulkGet(ids: string[]): Promise<T[]> {
		if (!ids.length) return [];
		const result = await this.db()?.allDocs({ keys: ids, include_docs: true });
		const docs = result?.rows
			.filter((row: any) => row.doc && !row.error)
			.map((row: any) => row.doc as T) ?? [];
		return docs.map((doc: T) => this.transformAfterLoad(doc));
	}

	/**
	 * Speichert mehrere Dokumente.
	 */
	async bulkDocs(docs: T[]): Promise<void> {
		if (!docs.length) return;
		if (!this.isLiveSync() && !this.oms.userOfflineMode() && !this.oms.networkOnline()) {
			throw new Error('Keine Netzwerkverbindung - Dokumente können nicht gespeichert werden');
		}
		const toSave = docs.map(doc => this.prepareDocumentForSave(doc));
		await this.db()?.bulkDocs(toSave);
		if (this.isLiveSync() || this.oms.userOfflineMode()) {
			this.hasPendingChanges.set(true);
		}
	}

	/**
	 * Löscht ein einzelnes Dokument.
	 */
	async remove(id: string) {
		// Im Online-Modus ohne Netzwerk: Löschen blockieren (nicht im Live-Sync)
		if (!this.isLiveSync() && !this.oms.userOfflineMode() && !this.oms.networkOnline()) {
			const errorMsg = "Keine Netzwerkverbindung - Dokument kann nicht gelöscht werden";
			if (this.msg) {
				this.msg.error(errorMsg);
			} else {
				console.error(errorMsg);
			}
			return;
		}
		const doc = await this.db()?.get(id);
		if (!doc) return;
		const result = await this.db()?.remove(id, doc._rev);
		// Markiere dass ungesynchronisierte Änderungen existieren
		if (result && (this.isLiveSync() || this.oms.userOfflineMode())) {
			this.hasPendingChanges.set(true);
		}
		return result;
	}

	/**
	 * Löscht mehrere Dokumente in einer Bulk-Operation.
	 */
	async removeBulk(ids: string[]): Promise<void> {
		if (!ids.length) return;
		// Im Online-Modus ohne Netzwerk: Löschen blockieren (nicht im Live-Sync)
		if (!this.isLiveSync() && !this.oms.userOfflineMode() && !this.oms.networkOnline()) {
			const errorMsg = "Keine Netzwerkverbindung - Dokumente können nicht gelöscht werden";
			if (this.msg) {
				this.msg.error(errorMsg);
			} else {
				console.error(errorMsg);
			}
			return;
		}
		const db = this.db();
		if (!db) return;

		// Alle Dokumente laden
		const result = await db.allDocs({ keys: ids, include_docs: true });
		const docsToDelete = result.rows
			.filter((row: any) => row.doc && !row.error)
			.map((row: any) => ({
				_id: row.doc._id,
				_rev: row.doc._rev,
				_deleted: true
			}));

		if (docsToDelete.length > 0) {
			await db.bulkDocs(docsToDelete);
			// Markiere dass ungesynchronisierte Änderungen existieren
			if (this.isLiveSync() || this.oms.userOfflineMode()) {
				this.hasPendingChanges.set(true);
			}
		}
	}

	// ========== Observable & Advanced Saving ==========

	/**
	 * Abonniert ein Dokument als Observable.
	 */
	getDocument$(id: string): Observable<T> {
		const db = this.db();
		if (!db) {
			return this.currentDb_.pipe(mergeMap((db: PouchDB.Database) => from(db.get(id)))) as Observable<T>;
		}
		return from(db.get(id)) as Observable<T>;
	}

	/**
	 * Speichert ein Dokument mit Toast-Benachrichtigung.
	 * DEPRECATED: Neue Apps sollten put() verwenden.
	 */
	saveDocument(doc: any, options?: SaveDocumentOptions) {
		const database = this.db();
		if (!database) {
			const errorMsg = "Keine Datenbankverbindung - Dokument kann nicht gespeichert werden";
			if (this.msg) {
				this.msg.error(errorMsg);
			} else {
				console.error(errorMsg);
			}
			console.error('saveDocument: keine DB verfügbar', { offline: this.oms.offlineMode(), remoteDb: !!this.remoteDb, localDb: !!this.localDb });
			return;
		}
		if (!this.isLiveSync() && !this.oms.userOfflineMode() && !this.oms.networkOnline()) {
			const errorMsg = "Keine Netzwerkverbindung - Dokument kann nicht gespeichert werden. Wechseln Sie in den Offline-Modus um lokal zu arbeiten.";
			if (this.msg) {
				this.msg.error(errorMsg);
			} else {
				console.error(errorMsg);
			}
			return;
		}

		const docToSave = this.prepareDocumentForSave(doc);
		const user = options?.user;
		if (user) {
			(docToSave as any).lastModified = { time: Date.now(), user };
			doc.lastModified = (docToSave as any).lastModified;
		}
		const target = this.oms.offlineMode() ? 'Lokale Datenbank' : 'Onlinedatenbank';
		database.get(doc._id).then((updatedDoc: any) => {
			const rev = updatedDoc._rev;
			(docToSave as any)._rev = rev;
			doc._rev = rev;
			database.put(docToSave).then((result: PouchDB.Core.Response) => {
				doc._rev = result.rev;
				this.documentSaved.emit(doc);
				if (this.msg) {
					this.msg.info(`Dokument wurde gespeichert (${target})`);
				}
				if (this.isLiveSync() || this.oms.userOfflineMode()) {
					this.hasPendingChanges.set(true);
				}
			}, (error: any) => {
				const errorMsg = "Fehler beim Speichern des Dokuments";
				if (this.msg) {
					this.msg.error(errorMsg);
				} else {
					console.error(errorMsg, error);
				}
			});
		}).catch((error: any) => {
			const errorMsg = "Fehler beim Laden des Dokuments vor dem Speichern";
			if (this.msg) {
				this.msg.error(errorMsg);
			} else {
				console.error(errorMsg, error);
			}
		});
	}

	/**
	 * Speichert ein Dokument ohne Toast-Meldung (für Bulk-Operationen).
	 * DEPRECATED: Neue Apps sollten put() verwenden.
	 */
	async saveDocumentQuiet(doc: any): Promise<void> {
		// Im Online-Modus ohne Netzwerk: Speichern blockieren (nicht im Live-Sync)
		if (!this.isLiveSync() && !this.oms.userOfflineMode() && !this.oms.networkOnline()) {
			throw new Error('Keine Netzwerkverbindung - Dokument kann nicht gespeichert werden');
		}
		const docToSave = this.prepareDocumentForSave(doc);
		try {
			const updatedDoc: any = await this.db()?.get(doc._id);
			(docToSave as any)._rev = updatedDoc._rev;
			doc._rev = updatedDoc._rev;
			const result = await this.db()?.put(docToSave);
			if (result) {
				doc._rev = result.rev;
				// Markiere dass ungesynchronisierte Änderungen existieren
				if (this.isLiveSync() || this.oms.userOfflineMode()) {
					this.hasPendingChanges.set(true);
				}
			}
		} catch (error) {
			console.error('Fehler beim Speichern:', error);
			throw error;
		}
	}

	// ========== Query ==========

	/**
	 * Query-Methode - muss von Subklassen implementiert werden wenn getAll() genutzt wird.
	 * BaseDataService nutzt 'application/index' View.
	 * BaseProjectService nutzt allDocs().
	 */
	async queryIndex(include_docs = false): Promise<any> {
		// Default: allDocs() - Subklassen können Views nutzen
		const database = this.db();
		if (!database) {
			console.warn('queryIndex: keine DB verfügbar', { offline: this.oms.offlineMode() });
			return null;
		}
		if (!this.isLiveSync() && !this.oms.userOfflineMode() && !this.oms.networkOnline()) {
			console.warn('queryIndex: Netzwerk nicht verfügbar');
			return null;
		}
		return await database.allDocs({ include_docs });
	}

	// ========== Download & Offline ==========

	/**
	 * Lädt spezifische Dokumente anhand ihrer IDs für Offline-Nutzung herunter.
	 * Die App bleibt im Online-Modus und nutzt weiterhin die Remote-Datenbank.
	 */
	public async downloadDocumentsByIds(docIds: string[]): Promise<void> {
		if (this.isLiveSync()) {
			console.warn('[downloadDocumentsByIds] Nicht verfügbar im Live-Sync-Modus');
			return;
		}
		if (!this.ps.project()) return;
		if (!docIds || docIds.length === 0) return;
		if (!this.remoteDb) {
			this.documentsDownloadCompleted.emit();
			return;
		}

		this.isBusy = true;

		// Erstelle lokale DB falls nicht vorhanden, ohne die aktive DB zu wechseln
		if (!this.localDb) {
			try {
				this.localDb = new PouchDB(this.ps.project());
				await this.localDb.info();
			} catch (err) {
				this.isBusy = false;
				this.documentsDownloadCompleted.emit();
				return;
			}
		}

		const replOptions: any = {
			batch_size: 10,
			live: false,
			retry: true,
			back_off_function: this.pouchDbBackoff,
			style: 'main_only',
			doc_ids: docIds
		};

		try {
			await new Promise<void>((resolve, reject) => {
				this.localDb!.replicate.from(this.remoteDb!, replOptions)
					.on('change', (result: any) => {
						this.setProgress(result);
					})
					.on('complete', () => {
						this.loadingProgress = 100;
						resolve();
					})
					.on('error', (err: any) => {
						reject(err);
					});
			});

			// Nach erfolgreichem Download: Design-Dokumente sicherstellen (Subklasse kann überschreiben)
			await this.afterDownloadComplete(this.localDb!);

			this._documentsDownloaded = true;
			// Status persistent speichern
			await this.storage.setProjectValue('pointsDownloaded', this.ps.project(), true);
			// Falls isInitialized false war, wieder aktivieren
			if (!this.isInitialized) {
				this.isInitialized = true;
				this.initialized.emit();
			}
			this.documentsDownloadCompleted.emit();
			this.checkedOut.emit();
		} catch {
			this.loadingProgress = 0;
			console.warn('sync error during download by ids');
		} finally {
			this.isBusy = false;
		}
	}

	/**
	 * Erstellt eine leere lokale Datenbank für Offline-Nutzung.
	 * Wird verwendet wenn keine Dokumente definiert sind.
	 */
	public async ensureEmptyOfflineDatabase(): Promise<void> {
		if (!this.ps.project()) return;

		if (!this.localDb) {
			try {
				this.localDb = new PouchDB(this.ps.project());
				await this.afterDownloadComplete(this.localDb);
			} catch (err) {
				return;
			}
		}

		this._documentsDownloaded = true;
		// Status persistent speichern
		await this.storage.setProjectValue('pointsDownloaded', this.ps.project(), true);
		// Falls isInitialized false war, wieder aktivieren
		if (!this.isInitialized) {
			this.isInitialized = true;
			this.initialized.emit();
		}
		this.documentsDownloadCompleted.emit();
		this.checkedOut.emit();
	}

	/**
	 * Hook: Wird nach erfolgreichem Download aufgerufen.
	 * Subklassen können hier Design-Dokumente erstellen.
	 */
	protected async afterDownloadComplete(db: PouchDB.Database): Promise<void> {
		// Default: keine Aktion
		// BaseDataService überschreibt dies um Design-Dokumente zu erstellen
	}

	/**
	 * Verwirft heruntergeladene Dokumente ohne den Offline-Modus zu beenden.
	 */
	override async discardLocal(): Promise<void> {
		// Persistent gespeicherten Status löschen
		await this.storage.setProjectValue('pointsDownloaded', this.ps.project(), false);
		if (this.localDb) {
			try {
				await this.localDb.destroy();
			} catch (err) {
				console.warn('Fehler beim Löschen der lokalen DB:', err);
			}
		}
		this.localDb = null;
		this._documentsDownloaded = false;
		this.downloadedDocumentsDiscarded.emit();
		this.discarded.emit();
	}

	/**
	 * Prüft, ob Dokumente für Offline-Nutzung heruntergeladen wurden (aktuelles Projekt).
	 * WICHTIG: Diese Methode ist synchron und basiert auf dem gecachten Flag.
	 */
	areDocumentsDownloaded(): boolean {
		if (this.isLiveSync()) return !!this.localDb;
		return this._documentsDownloaded && !!this.localDb;
	}

	/**
	 * Prüft asynchron, ob die lokale DB tatsächlich Dokumente enthält.
	 * Genauer als areDocumentsDownloaded(), aber langsamer.
	 */
	async areDocumentsActuallyDownloaded(): Promise<boolean> {
		if (!this.localDb) return false;
		try {
			const info = await this.localDb.info();
			return info.doc_count > 1;
		} catch {
			return false;
		}
	}

	/**
	 * Prüft, ob für ein bestimmtes Projekt Dokumente offline verfügbar sind.
	 * Verwendet den persistent gespeicherten Status für Stabilität.
	 */
	async areDocumentsDownloadedForProject(projectId: string): Promise<boolean> {
		if (!projectId) return false;
		const status = await this.storage.getProjectValue('pointsDownloaded', projectId);
		return status === true;
	}

	// ========== Sync Operations ==========

	/**
	 * Synchronisiert lokale Änderungen zur Remote-DB und bleibt im Offline-Modus.
	 * Die lokale DB bleibt erhalten.
	 */
	async checkinDocuments(): Promise<void> {
		if (this.isLiveSync()) return;
		if (!this.localDb || !this.oms.userOfflineMode())
			return;

		if (!this.remoteDb) return;

		this.isBusy = true;

		return new Promise((resolve, reject) => {
			PouchDB.replicate(this.localDb!, this.remoteDb!, { batch_size: 10 })
				.on('change', result => this.setProgress(result))
				.on('complete', () => {
					// Lokale DB bleibt erhalten, wir bleiben im Offline-Modus
					this.isBusy = false;
					this.checkedIn.emit();
					resolve();
				})
				.on('error', (err: any) => {
					this.isBusy = false;
					console.error(err);
					reject(err);
				});
		});
	}

	/**
	 * Beendet den Offline-Modus und wechselt zur Online-Datenbank.
	 * Synchronisiert zuerst, dann wird die lokale DB zerstört.
	 */
	async exitOfflineMode(): Promise<void> {
		if (this.isLiveSync()) return;
		if (!this.localDb || !this.oms.userOfflineMode())
			return;

		if (!this.remoteDb) return;

		this.isBusy = true;

		return new Promise((resolve, reject) => {
			PouchDB.replicate(this.localDb!, this.remoteDb!, { batch_size: 10 })
				.on('change', result => this.setProgress(result))
				.on('complete', async () => {
					// Lokale DB zerstören und zur Remote wechseln
					await this.localDb?.destroy();
					this.localDb = undefined;
					if (this.remoteDb) this.currentDb_.next(this.remoteDb);
					this.isBusy = false;
					this.checkedIn.emit();
					resolve();
				})
				.on('error', (err: any) => {
					this.isBusy = false;
					console.error(err);
					reject(err);
				});
		});
	}

	/**
	 * Synchronisiert lokale Änderungen aller Offline-Projekte zur Remote-Datenbank.
	 * Bei Konflikten gewinnt die lokale Version (force push).
	 * @returns Promise<boolean> true wenn alle erfolgreich
	 */
	async syncToRemote(): Promise<boolean> {
		// Remote-DB on-demand aufbauen (wichtig wenn aus Offline-Modus gesynced wird)
		if (!this.remoteDb) {
			const connected = await this.ensureRemoteDb();
			if (!connected) return false;
		}

		const projects = this.ps.projectList();
		const currentProjectId = this.ps.project();
		let allSuccess = true;

		// Ermittle alle Projekte mit lokalen Daten und initialisiere Fortschritt
		const projectsToSync: SyncProgress[] = [];
		const checkedProjectIds = new Set<string>();

		for (const project of projects) {
			checkedProjectIds.add(project._id);
			const hasLocalData = await this.areDocumentsDownloadedForProject(project._id);
			if (hasLocalData) {
				projectsToSync.push({
					projectId: project._id,
					projectName: project.name || project._id,
					status: 'pending',
					progress: 0
				});
			}
		}

		// Aktuelles Projekt prüfen falls nicht bereits geprüft
		if (currentProjectId && !checkedProjectIds.has(currentProjectId)) {
			const hasLocalData = await this.areDocumentsDownloadedForProject(currentProjectId);
			if (hasLocalData) {
				const currentProject = this.ps.currentProjectDoc();
				projectsToSync.push({
					projectId: currentProjectId,
					projectName: currentProject?.name || currentProjectId,
					status: 'pending',
					progress: 0
				});
			}
		}

		// Immer die Liste setzen, auch wenn leer
		this.syncProgressList.set(projectsToSync);

		if (projectsToSync.length === 0) return true;
		this.isSyncing.set(true);

		// Synchronisiere jedes Projekt
		for (let i = 0; i < projectsToSync.length; i++) {
			const syncItem = projectsToSync[i];
			this.updateSyncProgress(syncItem.projectId, { status: 'syncing', progress: 0 });

			try {
				await this.syncProjectDocumentsToRemote(syncItem.projectId);
				this.updateSyncProgress(syncItem.projectId, { status: 'done', progress: 100 });
			} catch (error) {
				console.error(`Fehler beim Synchronisieren von Projekt ${syncItem.projectId}:`, error);
				this.updateSyncProgress(syncItem.projectId, { status: 'error', progress: 0 });
				allSuccess = false;
			}
		}

		// Nach erfolgreicher Synchronisation: Pending-Flag zurücksetzen
		if (allSuccess) {
			this.hasPendingChanges.set(false);
		}

		this.isSyncing.set(false);
		return allSuccess;
	}

	/**
	 * Synchronisiert nur das aktuelle Projekt zur Remote-Datenbank.
	 * @returns Promise<boolean> true wenn erfolgreich
	 */
	async syncCurrentProjectToRemote(): Promise<boolean> {
		const projectId = this.ps.project();
		if (!projectId) return false;

		// Remote-DB on-demand aufbauen (wichtig wenn aus Offline-Modus gesynced wird)
		if (!this.remoteDb) {
			const connected = await this.ensureRemoteDb();
			if (!connected) return false;
		}

		// Prüfen ob lokale Daten vorhanden
		const hasLocalData = await this.areDocumentsDownloadedForProject(projectId);
		if (!hasLocalData) {
			// Nichts zu synchronisieren, aber pending-Flag trotzdem zurücksetzen
			this.hasPendingChanges.set(false);
			return true;
		}

		// Projektname aus der Liste holen
		const project = this.ps.projectList().find((p: any) => p._id === projectId);
		const projectName = project?.name || projectId;

		// Progress-Liste mit nur einem Eintrag
		this.syncProgressList.set([{
			projectId,
			projectName,
			status: 'pending',
			progress: 0
		}]);

		this.isSyncing.set(true);
		try {
			this.updateSyncProgress(projectId, { status: 'syncing', progress: 0 });
			await this.syncProjectDocumentsToRemote(projectId);
			this.updateSyncProgress(projectId, { status: 'done', progress: 100 });
			// Nach erfolgreicher Synchronisation: Pending-Flag zurücksetzen
			this.hasPendingChanges.set(false);
			return true;
		} catch (error) {
			console.error(`Fehler beim Synchronisieren von Projekt ${projectId}:`, error);
			this.updateSyncProgress(projectId, { status: 'error', progress: 0 });
			return false;
		} finally {
			this.isSyncing.set(false);
		}
	}

	/**
	 * Aktualisiert den Sync-Fortschritt für ein Projekt.
	 */
	private updateSyncProgress(projectId: string, update: Partial<SyncProgress>): void {
		this.syncProgressList.update(list =>
			list.map(item =>
				item.projectId === projectId ? { ...item, ...update } : item
			)
		);
	}

	/**
	 * Synchronisiert die Dokumente eines einzelnen Projekts zur Remote-Datenbank.
	 */
	private async syncProjectDocumentsToRemote(projectId: string): Promise<void> {
		if (!this.remoteDb) return;

		// Öffne die lokale DB für dieses Projekt
		const isCurrentProject = projectId === this.ps.project();
		const localDb = isCurrentProject && this.localDb
			? this.localDb
			: new PouchDB(projectId, { skip_setup: true });

		try {
			// Ermittle Gesamtanzahl der Dokumente für Fortschrittsanzeige
			const info = await localDb.info();
			const totalDocs = info.doc_count;

			// Repliziere lokal → remote mit Fortschritt und Timeout
			const SYNC_TIMEOUT_MS = 60000; // 60 Sekunden
			await new Promise<void>((resolve, reject) => {
				const timeoutId = setTimeout(() => {
					reject(new Error('Dokument-Synchronisation Timeout nach 60 Sekunden'));
				}, SYNC_TIMEOUT_MS);

				PouchDB.replicate(localDb, this.remoteDb!, { batch_size: 10 })
					.on('change', (result: any) => {
						const docsWritten = result.docs_written || 0;
						const progress = totalDocs > 0 ? Math.round((docsWritten / totalDocs) * 100) : 0;
						this.updateSyncProgress(projectId, {
							progress,
							docsWritten,
							docsTotal: totalDocs
						});
					})
					.on('complete', () => {
						clearTimeout(timeoutId);
						resolve();
					})
					.on('error', (err) => {
						clearTimeout(timeoutId);
						reject(err);
					});
			});

			// Konflikte auflösen (lokale Version gewinnt)
			const allDocs = await this.remoteDb.allDocs({ conflicts: true, include_docs: true });
			for (const row of allDocs.rows) {
				const doc = row.doc as any;
				if (doc?._conflicts && doc._conflicts.length > 0) {
					for (const conflictRev of doc._conflicts) {
						await this.remoteDb.remove(doc._id, conflictRev);
					}
				}
			}
		} finally {
			// Nur schließen wenn es nicht die aktuelle DB ist
			if (!isCurrentProject) {
				await localDb.close();
			}
		}
	}

	// ========== Live-Sync Integration ==========

	/**
	 * Reagiert auf Live-Sync-Änderungen:
	 * - Push-Richtung: hasPendingChanges zurücksetzen
	 * - Pull-Richtung: Refresh-Event emittieren
	 */
	protected override onLiveSyncChange(info: any): void {
		if (info.direction === 'push') {
			this.hasPendingChanges.set(false);
		}
		if (info.direction === 'pull') {
			this.documentsRefreshRequested.emit();
		}
	}

	// ========== Helper Methods (für Subklassen überschreibbar) ==========

	/**
	 * Transformiert ein Dokument nach dem Laden aus der DB.
	 * Default: keine Transformation. Subklassen können dies überschreiben.
	 */
	protected transformAfterLoad(doc: T): T {
		return doc;
	}

	/**
	 * Bereitet ein Dokument für das Speichern vor.
	 * Entfernt app-spezifische Properties und konvertiert Maps zu Objects.
	 */
	protected prepareDocumentForSave(doc: any): any {
		const docToSave = { ...doc };
		// Konvertiere Map zu Object für CouchDB
		if (docToSave.checkerComments && (docToSave.checkerComments instanceof Map)) {
			docToSave.checkerComments = Object.fromEntries(docToSave.checkerComments);
		}
		// Entferne transiente Properties
		delete docToSave.icon;
		delete docToSave.iconText;
		return docToSave;
	}
}
